import "../../css/GlobalStyles.css";
import {
  CSSProperties,
  ReactNode,
  memo,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { DropdownMenu, DropdownMenuItem } from "../DropdownMenu";
import { MixpanelContext } from "../../context/AnalyticsService";
import { getAccountState } from "../../redux/slices/accountSlice";
import MassTextPanel from "../CreatorDashboard/MassTexts/MassTextPanel";
import { useNavigate } from "../../hooks/useNavigate";
import { hasSubscriptionUnlockedAdvancedData } from "@markit/common.utils";
import { NavigationId } from "../../navigation/AppParamList";
import {
  MassTextSelectedType,
  MassTextsWrapperType,
} from "../CreatorDashboard/MassTexts/MassTextWrapperManager";
import AudienceListDetailsModal from "../CreatorDashboard/CreatorProfile/AudienceLists/AudienceListDetailsModal";
import { notificationActions } from "../../redux/slices/notificationSlice";
import CreateFormAndTrackingLinkModal from "../CreatorDashboard/FormSubmissions/CreateFormAndTrackingLinkModal";
import { PlanTypeV4, EventType, TeamMemberRole } from "@markit/common.types";
import RequestAccessModal from "../DisplayModal/RequestAccessModal";
import AddSingleContactModal from "../CreatorDashboard/CreatorProfile/ImportContacts/AddSingleContactModal";

type MainActionsDropdownMenuProps = {
  children: ReactNode;
  sourceLocation: string;
  overlayStyles?: CSSProperties;
};

const MainActionsDropdownMenu = memo(function MainActionsDropdownMenuFn(
  props: MainActionsDropdownMenuProps
) {
  const { children, sourceLocation, overlayStyles } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const mixpanel = useContext(MixpanelContext);
  const { accountData, teamMemberData } = useSelector(getAccountState).account;
  const [selectedTextType, setSelectedTextType] =
    useState<MassTextSelectedType>({ massText: undefined, isVisible: false });
  const [listPopupVisible, setListPopupVisible] = useState(false);
  const [createFormVisible, setCreateFormVisible] = useState(false);
  const [createTrackingLinkVisible, setCreateTrackingLinkVisible] =
    useState(false);
  const [addContactVisible, setAddContactVisible] = useState(false);
  const [requestAccessModal, setRequestAccessModal] = useState(false);

  const checkRequestAccess = useCallback(
    (callback: () => void) => {
      return () => {
        if (
          teamMemberData &&
          teamMemberData.role !== TeamMemberRole.FULL_ACCESS
        ) {
          setRequestAccessModal(true);
          return;
        }
        callback();
      };
    },
    [teamMemberData]
  );

  const newProductOnPress = useCallback(() => {
    mixpanel.track("Pressed Create New Product", {
      distinct_id: accountData.uid,
      button_location: "top header dropdown",
    });
    navigate(NavigationId.NEW_PRODUCT);
  }, [navigate, accountData.uid, mixpanel]);

  const createFormOnPress = useCallback(() => {
    mixpanel.track("Pressed Create Form Submission", {
      distinct_id: accountData.uid,
      platform: "webapp",
      button_location: sourceLocation,
    });
    setCreateFormVisible(true);
  }, [accountData.uid, mixpanel, sourceLocation]);

  const createGenericTrackingLinkOnPress = useCallback(() => {
    mixpanel.track("Pressed Create Generic Tracking Link", {
      distinct_id: accountData.uid,
      platform: "webapp",
      button_location: sourceLocation,
    });
    setCreateTrackingLinkVisible(true);
  }, [accountData.uid, mixpanel, sourceLocation]);

  const addSingleContactOnPress = useCallback(() => {
    setAddContactVisible(true);
  }, []);

  const dropdownItems: DropdownMenuItem[] = useMemo(
    () => [
      {
        title: "New Text",
        icon: "ion:paper-plane",
        key: "new_text",
        onPress: checkRequestAccess(() =>
          setSelectedTextType({ massText: undefined, isVisible: true })
        ),
      },
      {
        title: "Add Contact",
        icon: "ion:person-add",
        key: "add_contact",
        onPress: checkRequestAccess(addSingleContactOnPress),
      },
      {
        title: "Product",
        icon: "ion:bag-handle",
        key: "create_product",
        onPress: checkRequestAccess(newProductOnPress),
      },
      {
        title: "Form",
        icon: "ion:document-text",
        key: "create_form",
        onPress: checkRequestAccess(createFormOnPress),
      },
      {
        title: "Create Tracking Link",
        icon: "mdi:link-variant",
        key: "create_tracking_link",
        onPress: checkRequestAccess(createGenericTrackingLinkOnPress),
      },
      {
        title: "Import Contacts",
        icon: "ion:cloud-upload",
        key: "import_contacts",
        onPress: checkRequestAccess(() => {
          if (!hasSubscriptionUnlockedAdvancedData(accountData)) {
            dispatch(
              notificationActions.setMarkitPlusModal(PlanTypeV4.PRO.name)
            );
            return;
          }
          navigate("/import");
        }),
      },
    ],
    [
      accountData,
      addSingleContactOnPress,
      checkRequestAccess,
      createFormOnPress,
      createGenericTrackingLinkOnPress,
      dispatch,
      navigate,
      newProductOnPress,
    ]
  );

  return (
    <>
      <DropdownMenu
        dropdownItems={dropdownItems}
        placement="bottom"
        overlayStyle={overlayStyles}
      >
        {children}
      </DropdownMenu>
      {selectedTextType.isVisible ? (
        <MassTextPanel
          wrapperType={MassTextsWrapperType.MODAL}
          selectedTextType={selectedTextType}
          setSelectedTextType={setSelectedTextType}
          campaign={undefined}
        />
      ) : null}
      {listPopupVisible ? (
        <AudienceListDetailsModal
          isVisible={listPopupVisible}
          setIsVisible={setListPopupVisible}
          existingList={undefined}
        />
      ) : null}
      {createFormVisible || createTrackingLinkVisible ? (
        <CreateFormAndTrackingLinkModal
          isVisible={createFormVisible || createTrackingLinkVisible}
          setIsVisible={
            createFormVisible
              ? setCreateFormVisible
              : setCreateTrackingLinkVisible
          }
          eventType={
            createTrackingLinkVisible
              ? EventType.GENERIC_LINK_NO_DATA
              : EventType.GENERIC_LINK
          }
        />
      ) : null}
      {addContactVisible ? (
        <AddSingleContactModal setIsVisible={setAddContactVisible} />
      ) : null}
      {requestAccessModal ? (
        <RequestAccessModal
          isVisible={requestAccessModal}
          setIsVisible={setRequestAccessModal}
        />
      ) : null}
    </>
  );
});

export default MainActionsDropdownMenu;
